/*
 * Photoplan Shop — theme behaviours.
 *
 * Everything here is progressive enhancement: navigation, drawers and the cart
 * all work without JavaScript (plain links and native form posts). This file
 * only makes them nicer.
 */
(function () {
  'use strict';

  var theme = window.PPTheme || {};
  var routes = theme.routes || {};

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // init() runs again on shopify:section:load, so anything bound to document or
  // window goes through once() to avoid stacking duplicate listeners.
  var bound = {};

  function once(key, fn) {
    if (bound[key]) return;
    bound[key] = true;
    fn();
  }

  /* -------------------------------------------------------------------------
     Header: scroll state + expose the real height for sticky offsets
  ------------------------------------------------------------------------- */

  function setHeaderHeight() {
    var header = $('[data-pp-header]');
    if (!header) return;
    document.documentElement.style.setProperty('--pp-header-h', header.offsetHeight + 'px');
  }

  function setHeaderScrolled() {
    var header = $('[data-pp-header]');
    if (!header) return;
    header.setAttribute('data-scrolled', window.scrollY > 8 ? 'true' : 'false');
  }

  function initHeader() {
    setHeaderHeight();
    setHeaderScrolled();

    once('header', function () {
      window.addEventListener('scroll', setHeaderScrolled, { passive: true });
      window.addEventListener('resize', setHeaderHeight);
    });
  }

  /* -------------------------------------------------------------------------
     Desktop nav dropdowns

     Opened on hover for pointer devices and on focus/click for keyboard and
     touch, so the mega-menu is reachable either way.
  ------------------------------------------------------------------------- */

  function initNavDropdowns() {
    var items = $$('[data-pp-nav-item]');
    if (!items.length) return;

    function close(item) {
      item.setAttribute('data-open', 'false');
      var trigger = $('[data-pp-nav-trigger]', item);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }

    function open(item) {
      items.forEach(function (other) {
        if (other !== item) close(other);
      });
      item.setAttribute('data-open', 'true');
      var trigger = $('[data-pp-nav-trigger]', item);
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }

    items.forEach(function (item) {
      var trigger = $('[data-pp-nav-trigger]', item);

      item.addEventListener('mouseenter', function () {
        if (window.matchMedia('(hover: hover)').matches) open(item);
      });

      item.addEventListener('mouseleave', function () {
        if (window.matchMedia('(hover: hover)').matches) close(item);
      });

      item.addEventListener('focusin', function () {
        open(item);
      });

      item.addEventListener('focusout', function (event) {
        if (!item.contains(event.relatedTarget)) close(item);
      });

      if (trigger) {
        trigger.addEventListener('click', function (event) {
          var isOpen = item.getAttribute('data-open') === 'true';
          // A trigger that is also a link should still navigate on a second tap.
          if (!isOpen) {
            event.preventDefault();
            open(item);
          }
        });
      }
    });

    once('navEscape', function () {
      document.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') return;
        $$('[data-pp-nav-item][data-open="true"]').forEach(close);
      });
    });
  }

  /* -------------------------------------------------------------------------
     Drawers (mobile menu, cart, search)
  ------------------------------------------------------------------------- */

  var lastFocused = null;

  function openDrawer(id) {
    var drawer = document.getElementById(id);
    if (!drawer) return;
    lastFocused = document.activeElement;
    drawer.setAttribute('data-open', 'true');
    document.body.setAttribute('data-drawer-open', 'true');
    var focusable = $('[data-pp-drawer-focus], button, a[href], input', drawer);
    if (focusable) {
      window.setTimeout(function () {
        focusable.focus();
      }, 60);
    }
  }

  function closeDrawer(drawer) {
    if (!drawer) return;
    drawer.setAttribute('data-open', 'false');
    if (!$('[data-pp-drawer][data-open="true"]')) {
      document.body.removeAttribute('data-drawer-open');
    }
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  function initDrawers() {
    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-pp-drawer-open]');
      if (opener) {
        event.preventDefault();
        openDrawer(opener.getAttribute('data-pp-drawer-open'));
        return;
      }

      var closer = event.target.closest('[data-pp-drawer-close]');
      if (closer) {
        event.preventDefault();
        closeDrawer(closer.closest('[data-pp-drawer]'));
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      closeDrawer($('[data-pp-drawer][data-open="true"]'));
    });
  }

  /* -------------------------------------------------------------------------
     Collapsibles: mobile nav sections and FAQ accordions
  ------------------------------------------------------------------------- */

  function initCollapsibles() {
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-pp-collapse]');
      if (!trigger) return;

      var panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;

      var isOpen = trigger.getAttribute('aria-expanded') === 'true';

      // Accordions marked exclusive close their siblings.
      var group = trigger.closest('[data-pp-collapse-group]');
      if (group && !isOpen) {
        $$('[data-pp-collapse]', group).forEach(function (other) {
          if (other === trigger) return;
          other.setAttribute('aria-expanded', 'false');
          var otherPanel = document.getElementById(other.getAttribute('aria-controls'));
          if (otherPanel) otherPanel.hidden = true;
        });
      }

      trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      panel.hidden = isOpen;
    });
  }

  /* -------------------------------------------------------------------------
     Product media gallery
  ------------------------------------------------------------------------- */

  function initGalleries() {
    $$('[data-pp-gallery]').forEach(function (gallery) {
      var slides = $$('[data-pp-gallery-slide]', gallery);
      var thumbs = $$('[data-pp-gallery-thumb]', gallery);
      var counter = $('[data-pp-gallery-counter]', gallery);
      if (slides.length < 1) return;

      var index = 0;

      function show(next) {
        index = (next + slides.length) % slides.length;
        slides.forEach(function (slide, i) {
          slide.setAttribute('data-active', i === index ? 'true' : 'false');
          // Pause any video that scrolls out of view.
          if (i !== index) {
            var video = slide.querySelector('video');
            if (video && !video.paused) video.pause();
          }
        });
        thumbs.forEach(function (thumb, i) {
          thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
        if (counter) counter.textContent = index + 1 + ' / ' + slides.length;
      }

      thumbs.forEach(function (thumb, i) {
        thumb.addEventListener('click', function () {
          show(i);
        });
      });

      var prev = $('[data-pp-gallery-prev]', gallery);
      var next = $('[data-pp-gallery-next]', gallery);
      if (prev) {
        prev.addEventListener('click', function () {
          show(index - 1);
        });
      }
      if (next) {
        next.addEventListener('click', function () {
          show(index + 1);
        });
      }

      // Let the configurator jump to the image belonging to a chosen variant.
      gallery.addEventListener('pp:gallery:show', function (event) {
        var mediaId = event.detail && event.detail.mediaId;
        if (!mediaId) return;
        for (var i = 0; i < slides.length; i++) {
          if (slides[i].getAttribute('data-media-id') === String(mediaId)) {
            show(i);
            return;
          }
        }
      });

      show(0);
    });
  }

  /* -------------------------------------------------------------------------
     Quantity steppers
  ------------------------------------------------------------------------- */

  function initQuantity() {
    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-pp-qty-change]');
      if (!button) return;
      event.preventDefault();

      var wrapper = button.closest('[data-pp-qty]');
      var input = $('input', wrapper);
      if (!input) return;

      var step = parseInt(button.getAttribute('data-pp-qty-change'), 10) || 0;
      var min = parseInt(input.getAttribute('min'), 10);
      if (isNaN(min)) min = 1;
      var value = (parseInt(input.value, 10) || min) + step;
      input.value = Math.max(min, value);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* -------------------------------------------------------------------------
     Cart
  ------------------------------------------------------------------------- */

  // This shop's money format is "&pound;{{amount}}". Prices are written with
  // textContent, so the entity has to be decoded or it shows up raw.
  var decodedMoneyFormat = null;

  function moneyFormat() {
    if (decodedMoneyFormat === null) {
      var raw = theme.moneyFormat || '£{{amount}}';
      if (raw.indexOf('&') === -1) {
        decodedMoneyFormat = raw;
      } else {
        var decoder = document.createElement('textarea');
        decoder.innerHTML = raw;
        decodedMoneyFormat = decoder.value;
      }
    }
    return decodedMoneyFormat;
  }

  function formatMoney(cents) {
    var format = moneyFormat();
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var withCommas = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');

    return format
      .replace(/\{\{\s*amount\s*\}\}/, withCommas + '.' + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withCommas + ',' + parts[1])
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, withCommas);
  }

  function updateCartCount(count) {
    $$('[data-pp-cart-count]').forEach(function (node) {
      node.textContent = count;
      node.hidden = count === 0;
    });
  }

  /** Swap in freshly rendered markup returned by the Section Rendering API. */
  function replaceSection(html, sectionId, selector) {
    var host = $(selector);
    if (!host || !html) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var fresh = $(selector, parsed);
    if (fresh) host.innerHTML = fresh.innerHTML;
  }

  function refreshCartSections(sections) {
    if (!sections) return;
    if (sections['pp-cart-drawer']) {
      replaceSection(sections['pp-cart-drawer'], 'pp-cart-drawer', '[data-pp-cart-drawer-contents]');
    }
  }

  function initAddToCart() {
    document.addEventListener('submit', function (event) {
      var form = event.target.closest('form[action*="/cart/add"]');
      if (!form) return;

      // The appointments app takes over this button to collect a time slot.
      // When it has, stay out of the way and let it run its own flow.
      if ($('[data-servicify-atc]', form)) return;

      var submit = $('[data-pp-cart-submit]', form);
      var errorBox = $('[data-pp-cart-error]', form);
      var configurator = form.closest('[data-pp-configurator]');
      var boltOns = configurator
        ? $$('[data-pp-bolt-on-input]:checked', configurator)
        : [];
      var drawer = $('[data-pp-cart-drawer]');

      if (boltOns.length && typeof addItemsToCart === 'function') {
        event.preventDefault();
        var variantInput = $('[data-pp-variant-id]', form);
        var quantityInput = $('input[name="quantity"]', form);
        var addonNames = boltOns.map(function (input) {
          return input.getAttribute('data-pp-bolt-on-name');
        });
        var items = [
          {
            id: variantInput ? variantInput.value : null,
            quantity: quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1,
            properties: {
              'Bolt-ons': addonNames.join(', ')
            }
          }
        ];
        boltOns.forEach(function (input) {
          items.push({
            id: input.getAttribute('data-pp-bolt-on-id'),
            quantity: 1,
            properties: {
              'Added with': configurator.getAttribute('data-product-title') || document.title
            }
          });
        });

        if (submit) submit.setAttribute('aria-busy', 'true');
        if (errorBox) errorBox.setAttribute('data-visible', 'false');

        addItemsToCart(items)
          .catch(function (error) {
            if (errorBox) {
              errorBox.textContent = error.message || theme.strings.cartError;
              errorBox.setAttribute('data-visible', 'true');
            }
          })
          .finally(function () {
            if (submit) submit.removeAttribute('aria-busy');
          });
        return;
      }

      if (!drawer) return;

      event.preventDefault();

      var body = new FormData(form);
      body.append('sections', 'pp-cart-drawer');
      body.append('sections_url', window.location.pathname);

      if (submit) submit.setAttribute('aria-busy', 'true');
      if (errorBox) errorBox.setAttribute('data-visible', 'false');

      fetch(routes.cartAdd || '/cart/add', {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: body
      })
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          if (data.status) {
            throw new Error(data.description || data.message);
          }
          refreshCartSections(data.sections);
          return fetch((routes.cart || '/cart') + '.js', {
            headers: { Accept: 'application/json' }
          }).then(function (r) {
            return r.json();
          });
        })
        .then(function (cart) {
          updateCartCount(cart.item_count);
          openDrawer('pp-cart-drawer');
        })
        .catch(function (error) {
          if (errorBox) {
            errorBox.textContent = error.message || theme.strings.cartError;
            errorBox.setAttribute('data-visible', 'true');
          } else {
            form.submit();
          }
        })
        .finally(function () {
          if (submit) submit.removeAttribute('aria-busy');
        });
    });
  }

  /**
   * Add one or more variants (used by the residential package picker so a
   * package and its bolt-ons land in the basket as a single configuration).
   */
  function addItemsToCart(items) {
    return fetch((routes.cartAdd || '/cart/add') + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: items,
        sections: 'pp-cart-drawer',
        sections_url: window.location.pathname
      })
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (data) {
        if (data.status) {
          throw new Error(data.description || data.message);
        }
        refreshCartSections(data.sections);
        return fetch((routes.cart || '/cart') + '.js', {
          headers: { Accept: 'application/json' }
        }).then(function (r) {
          return r.json();
        });
      })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        openDrawer('pp-cart-drawer');
        return cart;
      });
  }

  function changeCartLine(key, quantity) {
    return fetch(routes.cartChange || '/cart/change', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: key,
        quantity: quantity,
        sections: 'pp-cart-drawer',
        sections_url: window.location.pathname
      })
    }).then(function (response) {
      return response.json();
    });
  }

  function initCartLineControls() {
    document.addEventListener('click', function (event) {
      var remove = event.target.closest('[data-pp-cart-remove]');
      if (!remove) return;
      event.preventDefault();

      // On the cart page let the normal link work so the page re-renders.
      if (!remove.closest('[data-pp-cart-drawer]')) return;

      changeCartLine(remove.getAttribute('data-pp-cart-remove'), 0).then(function (cart) {
        refreshCartSections(cart.sections);
        updateCartCount(cart.item_count);
      });
    });

    document.addEventListener('change', function (event) {
      var input = event.target.closest('[data-pp-cart-qty]');
      if (!input || !input.closest('[data-pp-cart-drawer]')) return;

      changeCartLine(
        input.getAttribute('data-pp-cart-qty'),
        parseInt(input.value, 10) || 0
      ).then(function (cart) {
        refreshCartSections(cart.sections);
        updateCartCount(cart.item_count);
      });
    });
  }

  /* -------------------------------------------------------------------------
     Sticky mobile price bar — shown once the booking card scrolls away
  ------------------------------------------------------------------------- */

  function initStickyBar() {
    var bar = $('[data-pp-sticky-bar]');
    var anchor = $('[data-pp-sticky-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        bar.setAttribute('data-visible', entries[0].isIntersecting ? 'false' : 'true');
      },
      { rootMargin: '-30% 0px 0px 0px' }
    );

    observer.observe(anchor);
  }

  /* -------------------------------------------------------------------------
     Reveal-on-scroll for section content
  ------------------------------------------------------------------------- */

  function initReveal() {
    var targets = $$('[data-pp-reveal]');
    if (!targets.length) return;

    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (node) {
        node.classList.add('pp-fade-up');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('pp-fade-up');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px' }
    );

    targets.forEach(function (node) {
      observer.observe(node);
    });
  }

  /* -------------------------------------------------------------------------
     Account pages — destructive-action confirmation and province selects
  ------------------------------------------------------------------------- */

  function initConfirmButtons() {
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-pp-confirm]');
      if (!trigger) return;
      if (!window.confirm(trigger.getAttribute('data-pp-confirm'))) {
        event.preventDefault();
      }
    });
  }

  // Shopify's country_option_tags carry a data-provinces JSON array, so the
  // province field can be populated without an extra request.
  function initProvinceSelects() {
    $$('select[name="address[country]"]').forEach(function (countrySelect) {
      if (countrySelect.hasAttribute('data-pp-bound')) return;
      countrySelect.setAttribute('data-pp-bound', 'true');

      var wrapper = document.getElementById(
        countrySelect.id.replace(/-country$/, '-province-wrapper')
      );
      var provinceSelect = document.getElementById(
        countrySelect.id.replace(/-country$/, '-province')
      );
      if (!wrapper || !provinceSelect) return;

      var defaultCountry = countrySelect.getAttribute('data-default');
      if (defaultCountry) countrySelect.value = defaultCountry;

      function sync() {
        var option = countrySelect.options[countrySelect.selectedIndex];
        var provinces = [];

        try {
          provinces = JSON.parse(option.getAttribute('data-provinces') || '[]');
        } catch (error) {
          provinces = [];
        }

        provinceSelect.innerHTML = '';

        if (!provinces.length) {
          wrapper.hidden = true;
          return;
        }

        provinces.forEach(function (province) {
          var opt = document.createElement('option');
          opt.value = province[0];
          opt.textContent = province[1];
          provinceSelect.appendChild(opt);
        });

        var defaultProvince = provinceSelect.getAttribute('data-default');
        if (defaultProvince) provinceSelect.value = defaultProvince;
        wrapper.hidden = false;
      }

      countrySelect.addEventListener('change', sync);
      sync();
    });
  }

  function initReviewCarousels() {
    document.querySelectorAll('[data-pp-review-carousel]').forEach(function (root) {
      if (root.dataset.ppBound === 'true') return;
      root.dataset.ppBound = 'true';

      var slides = Array.prototype.slice.call(
        root.querySelectorAll('[data-pp-review-slide]')
      );
      if (slides.length < 2) return;

      var index = 0;
      var paused = false;
      var reduceMotion =
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var interval = parseInt(root.getAttribute('data-interval') || '5500', 10);

      function show(next) {
        slides[index].hidden = true;
        slides[index].classList.remove('is-animating');
        index = next;
        slides[index].hidden = false;
        if (!reduceMotion) {
          // Restart the fade animation.
          void slides[index].offsetWidth;
          slides[index].classList.add('is-animating');
        }
      }

      root.addEventListener('mouseenter', function () {
        paused = true;
      });
      root.addEventListener('mouseleave', function () {
        paused = false;
      });
      root.addEventListener('focusin', function () {
        paused = true;
      });
      root.addEventListener('focusout', function () {
        paused = false;
      });

      if (reduceMotion) return;

      window.setInterval(function () {
        if (paused || document.hidden) return;
        show((index + 1) % slides.length);
      }, interval);
    });
  }

  function initWhatsAppTracking() {
    var link = document.querySelector('[data-pp-whatsapp]');
    if (!link) return;

    link.addEventListener('click', function () {
      if (typeof window.gtag !== 'function') return;
      try {
        window.gtag('event', 'whatsapp_click', {
          site: 'photoplanshop.com',
          placement: 'floating'
        });
      } catch (err) {}
    });
  }

  function init() {
    initHeader();
    initNavDropdowns();
    once('drawers', initDrawers);
    once('collapsibles', initCollapsibles);
    once('quantity', initQuantity);
    once('addToCart', initAddToCart);
    once('cartLines', initCartLineControls);
    once('confirm', initConfirmButtons);
    initGalleries();
    initStickyBar();
    initReveal();
    initProvinceSelects();
    initReviewCarousels();
    once('whatsapp', initWhatsAppTracking);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose the few helpers the configurator and Shopify's theme editor need.
  window.PPTheme = Object.assign(theme, {
    formatMoney: formatMoney,
    openDrawer: openDrawer,
    addItemsToCart: addItemsToCart,
    refresh: init
  });

  document.addEventListener('shopify:section:load', init);
})();
